import { cloneDeep } from 'lodash';
import { Block, BlockType } from 'src/config/api/models/Block';

export const moveBlockInsideList = (
  list: Block[],
  sourcePosition: number,
  destinationPosition: number
): Block[] => {
  const newList = [...list];
  const blockToMove = newList[sourcePosition];
  newList.splice(sourcePosition, 1);
  newList.splice(destinationPosition, 0, blockToMove);
  return newList;
};

export const moveBlock = (
  source: {
    type: 'cluster' | 'clusterBlock' | 'pageBlock';
    clusterId: string;
    sectionId: string;
    index: number;
  },
  destination: {
    type: 'cluster' | 'clusterBlock' | 'pageBlock';
    clusterId: string;
    sectionId: string;
    index: number;
  },
  pageId: string,
  pageBlocksList: Block[] | undefined
) => {
  if (!pageBlocksList) return;

  //#region Page to Page
  // Move blocks at page level
  if (source.type !== 'clusterBlock' && destination.type !== 'clusterBlock') {
    const newBlockList = moveBlockInsideList(pageBlocksList, source.index, destination.index);
    return newBlockList;
  }
  //#endregion

  //#region Same Section
  // Move blocks inside the same section - lets assume sectionIds are really unique!!!
  if (
    source.type === 'clusterBlock' &&
    destination.type === 'clusterBlock' &&
    source.sectionId === destination.sectionId
  ) {
    const newBlockList = structuredClone(pageBlocksList);
    const cluster = newBlockList.find((c) => c.id === source.clusterId);
    if (!cluster || cluster.type !== 'cluster' || !cluster.sections) return;
    const clusterIndex = pageBlocksList.indexOf(cluster);
    const section = cluster.sections?.find((s) => s.id === source.sectionId);
    if (!section || !section.childBlocks) return;
    const sectionIndex = cluster.sections.indexOf(section);
    const sectionChildBlocksReordered = moveBlockInsideList(
      section.childBlocks,
      source.index,
      destination.index
    );
    section.childBlocks = sectionChildBlocksReordered;
    cluster.sections.splice(sectionIndex, 1, section);
    newBlockList[clusterIndex] = cluster;
    return newBlockList;
  }
  // #endregion

  //#region Page to Section
  // move from the page to a section
  if (source.type === 'pageBlock') {
    const newBlockList = structuredClone(pageBlocksList);
    const blockToMove = newBlockList[source.index];
    newBlockList.splice(source.index, 1);

    /***************************************************************/
    const destinationCluster = newBlockList.find((c) => c.id === destination.clusterId);
    if (
      !destinationCluster ||
      destinationCluster.type !== 'cluster' ||
      !destinationCluster.sections
    ) {
      return null;
    }
    const destinationclusterIndex = newBlockList.indexOf(destinationCluster);

    /***************************************************************/

    const destinationSection = destinationCluster.sections.find(
      (s) => s.id === destination.sectionId
    );

    if (!destinationSection || !destinationSection.childBlocks) {
      return null;
    }
    const destinationSectionIndex = destinationCluster.sections.indexOf(destinationSection);

    /**************************************************************/

    const destinationSectionChildBlocks = [...destinationSection.childBlocks];
    destinationSectionChildBlocks.splice(destination.index, 0, blockToMove);
    destinationSection.childBlocks = destinationSectionChildBlocks;
    destinationCluster.sections.splice(destinationSectionIndex, 1, destinationSection);
    newBlockList[destinationclusterIndex] = destinationCluster;
    return newBlockList;
  }
  //#endregion

  //#region Section to Page
  // move from a section to the page
  if (destination.type === 'pageBlock') {
    const newBlockList = structuredClone(pageBlocksList);
    const sourceCluster = newBlockList.find((c) => c.id === source.clusterId);
    if (!sourceCluster || sourceCluster.type !== 'cluster' || !sourceCluster.sections) return;
    const sourceClusterIndex = newBlockList.indexOf(sourceCluster);

    const sourceSection = sourceCluster.sections.find((s) => s.id === source.sectionId);
    if (!sourceSection || !sourceSection.childBlocks) return;
    const sourceSectionIndex = sourceCluster.sections.indexOf(sourceSection);

    const sourceSectionChildBlocks = [...sourceSection.childBlocks];
    const blockToMove = sourceSectionChildBlocks[source.index];
    sourceSectionChildBlocks.splice(source.index, 1);
    sourceSection.childBlocks = sourceSectionChildBlocks;
    sourceCluster.sections.splice(sourceSectionIndex, 1, sourceSection);

    newBlockList[sourceClusterIndex] = sourceCluster;
    newBlockList.splice(destination.index, 0, blockToMove);
    return newBlockList;
  }

  //#region Same Cluster
  // move from a section to the page
  if (
    source.type === 'clusterBlock' &&
    destination.type === 'clusterBlock' &&
    source.clusterId === destination.clusterId
  ) {
    const newBlockList = structuredClone(pageBlocksList);
    const cluster = newBlockList.find((c) => c.id === source.clusterId);
    if (!cluster || cluster.type !== 'cluster' || !cluster.sections) return;
    const clusterIndex = newBlockList.indexOf(cluster);

    const sourceSection = cluster.sections.find((s) => s.id === source.sectionId);
    if (!sourceSection || !sourceSection.childBlocks) return;
    const sourceSectionIndex = cluster.sections.indexOf(sourceSection);

    const destinationSection = cluster.sections.find((s) => s.id === destination.sectionId);
    if (!destinationSection || !destinationSection.childBlocks) return;
    const destinationSectionIndex = cluster.sections.indexOf(destinationSection);

    const sourceSectionChildBlocks = [...sourceSection.childBlocks];
    const blockToMove = sourceSectionChildBlocks[source.index];
    sourceSectionChildBlocks.splice(source.index, 1);
    sourceSection.childBlocks = sourceSectionChildBlocks;

    const destinationSectionChildBlocks = [...destinationSection.childBlocks];
    destinationSectionChildBlocks.splice(destination.index, 0, blockToMove);
    destinationSection.childBlocks = destinationSectionChildBlocks;

    cluster.sections[sourceSectionIndex] = sourceSection;
    cluster.sections[destinationSectionIndex] = destinationSection;

    newBlockList[clusterIndex] = cluster;
    return newBlockList;
  }

  //#region Different Clusters
  // move from a section to the page
  if (
    source.type === 'clusterBlock' &&
    destination.type === 'clusterBlock' &&
    source.clusterId !== destination.clusterId
  ) {
    const newBlockList = structuredClone(pageBlocksList);
    const sourceCluster = newBlockList.find((c) => c.id === source.clusterId);
    if (!sourceCluster || sourceCluster.type !== 'cluster' || !sourceCluster.sections) return;
    const sourceClusterIndex = newBlockList.indexOf(sourceCluster);

    const sourceSection = sourceCluster.sections.find((s) => s.id === source.sectionId);
    if (!sourceSection || !sourceSection.childBlocks) return;
    const sourceSectionIndex = sourceCluster.sections.indexOf(sourceSection);

    const destinationCluster = newBlockList.find((c) => c.id === destination.clusterId);
    if (
      !destinationCluster ||
      destinationCluster.type !== 'cluster' ||
      !destinationCluster.sections
    )
      return;
    const destinationClusterIndex = newBlockList.indexOf(destinationCluster);

    const destinationSection = destinationCluster.sections.find(
      (s) => s.id === destination.sectionId
    );
    if (!destinationSection || !destinationSection.childBlocks) return;
    const destinationSectionIndex = destinationCluster.sections.indexOf(destinationSection);

    const sourceSectionChildBlocks = [...sourceSection.childBlocks];
    const blockToMove = sourceSectionChildBlocks[source.index];
    sourceSectionChildBlocks.splice(source.index, 1);
    sourceSection.childBlocks = sourceSectionChildBlocks;

    const destinationSectionChildBlocks = [...destinationSection.childBlocks];
    destinationSectionChildBlocks.splice(destination.index, 0, blockToMove);
    destinationSection.childBlocks = destinationSectionChildBlocks;

    sourceCluster.sections[sourceSectionIndex] = sourceSection;
    destinationCluster.sections[destinationSectionIndex] = destinationSection;

    newBlockList[sourceClusterIndex] = sourceCluster;
    newBlockList[destinationClusterIndex] = destinationCluster;
    return newBlockList;
  }
};

/* CLUSTERS */

const clusterGenerator = (childBlocks: Block[]) => ({
  id: '0000 0000 0000 0000 0000 0000',
  type: 'cluster' as BlockType,
  title: undefined,
  bloom_type: undefined,
  url: undefined,
  client_settings: undefined,
  is_visible: true,
  algebra_kit_exercise_id: undefined,
  parent_id: undefined,
  is_printable: undefined,
  content: '',
  exercise_tool: null,
  childBlocks: childBlocks,
});

export const createClusterFromCluster = (
  currentBlocksList: Block[],
  originClusterId: string,
  previousBlockId: string
): { result: Block[]; index: number } | null => {
  const newCluster = clusterGenerator([]);
  const clonedBlockList = cloneDeep(currentBlocksList);
  const existingCluster = clonedBlockList?.find((b: Block) => b.id === originClusterId);
  if (!existingCluster) return null;
  else {
    const existingClusterBlockIndex = clonedBlockList.indexOf(existingCluster);
    const existingClusterChildrenList = [...existingCluster.childBlocks!];
    const blockBeforeNewCluster = existingClusterChildrenList.find(
      (b: Block) => b.id === previousBlockId
    );
    const blockBeforeNewClusterIndex = existingClusterChildrenList.indexOf(blockBeforeNewCluster!);
    const newClusterChildList = existingClusterChildrenList.splice(
      blockBeforeNewClusterIndex + 1,
      existingClusterChildrenList.length - blockBeforeNewClusterIndex + 1
    );
    existingCluster.childBlocks = existingClusterChildrenList;
    newCluster.childBlocks = newClusterChildList;
    clonedBlockList.splice(existingClusterBlockIndex, 1, existingCluster, newCluster);
    return { result: clonedBlockList, index: existingClusterBlockIndex + 1 };
  }
};

export const createClusterFromPage = (
  currentBlocksList: Block[],
  previousBlockId: string
): { result: Block[]; index: number } | null => {
  const newCluster = clusterGenerator([]);
  // no blocks in page
  if (currentBlocksList.length === 0) {
    const newBlockList = [newCluster] as Block[];
    return { result: newBlockList, index: 0 };
  } else {
    const newBlockList = cloneDeep(currentBlocksList);
    const beforeClusterBlock = newBlockList?.find((b: Block) => b.id === previousBlockId);
    // could not found the previous block
    if (!beforeClusterBlock) {
      return null;
    } else {
      const positionToCreateClusterIndex = newBlockList?.indexOf(beforeClusterBlock) + 1;
      const nonClusterBlocksSearchArray = newBlockList.slice(positionToCreateClusterIndex);
      const lastNonClusterBlock = nonClusterBlocksSearchArray.find((b) => b.type === 'cluster');
      // no more clusters on the page, all following blocks will be children
      if (!lastNonClusterBlock) {
        newCluster.childBlocks = nonClusterBlocksSearchArray;
        newBlockList.splice(
          positionToCreateClusterIndex,
          nonClusterBlocksSearchArray.length,
          newCluster
        );
      } else {
        // found another cluster, we want the blocks before that
        const lastNonClusterBlockIndex = nonClusterBlocksSearchArray.indexOf(lastNonClusterBlock);
        const blocksToClusterChildren = nonClusterBlocksSearchArray.slice(
          0,
          lastNonClusterBlockIndex
        );
        newCluster.childBlocks = blocksToClusterChildren;
        newBlockList.splice(
          positionToCreateClusterIndex,
          blocksToClusterChildren.length,
          newCluster
        );
      }
      return { result: newBlockList, index: positionToCreateClusterIndex };
    }
  }
};

/*const createClusterFromClusterJS = () => {
    let newBlockState: Block[] = [];
    const clonedBlockState = cloneDeep(pageBlocksList);

    clonedBlockState?.forEach((b) => {
      if (b.id === ownerId && b.childBlocks) {
        const foundOriginCluster = b.childBlocks.find((bb) => bb.id === previousBlockId);
        if (foundOriginCluster) {
          const splitIndex = b.childBlocks?.indexOf(foundOriginCluster);
          console.log('Split index: ', splitIndex);
          const remainingClusterChildren = b.childBlocks && [...b.childBlocks];
          const keptClusterChildren = remainingClusterChildren.splice(0, splitIndex + 1);
          b.childBlocks = keptClusterChildren;
          console.log('results: ', keptClusterChildren, remainingClusterChildren);
          newBlockState.push(b);
          const newCluster: Block = clusterGenerator(remainingClusterChildren);
          newBlockState.push(newCluster);
        } else {
          newBlockState.push(b);
        }
      } else {
        newBlockState.push(b);
      }
    });

    console.log('New block state: ', newBlockState);
    dispatch(updateBlocksList(newBlockState));
  };*/

/*const createClusterFromPageJS = () => {
    let injectIntoCluster = false;
    let clusterChildren: Block[] = [];
    let newBlockState: Block[] = [];
    let clusterIndex = -1;
    const clonedBlockState = cloneDeep(pageBlocksList);

    clonedBlockState?.forEach((b, index) => {
      console.log('b ', b as Block);
      if (injectIntoCluster && b.type === 'cluster') {
        injectIntoCluster = false;
        newBlockState.push(b as Block);
      } else if (injectIntoCluster && b.type !== 'cluster') {
        clusterChildren.push(b);
      } else if (previousBlockId === b.id) {
        const newCluster = clusterGenerator([]);
        newBlockState.push(b as Block);
        newBlockState.push(newCluster);
        clusterIndex = index + 1;
        injectIntoCluster = true;
      } else newBlockState.push(b as Block);
    });

    if (clusterIndex) newBlockState[clusterIndex].childBlocks = clusterChildren;

    console.log('New block state: ', newBlockState);
    dispatch(updateBlocksList(newBlockState));
    // dispatch(createCluster({ beforeBlockId: previousBlockId, pageId }));
  };*/
